import { TableCell } from "@mui/material";
import { useCallback, useMemo, type FC } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  bulkApprovalBookingConflictsAtom,
  bulkApprovalBookingConflictsUpdatedAtom,
  DialogType,
  getBookingCalendarCellId,
  useDialogs,
  type BookingCellId,
  type BookingConflictErrorType,
  type DateYMDString,
  type DirectionEnum,
  type IApprovalBookingConflict,
  type ReasonDtoDropdownItem,
  type SimpleResult,
} from "@ryde/shared";

interface IRejectedBookingApprovalButtonTableCellProps {
  id: number;
  passengerId: number;
  passengerFullName: string;
  direction?: DirectionEnum;
  date: DateYMDString;
  errorType: BookingConflictErrorType;
  /**
   * Which of the cell's conflicts the approval covers. The default approves the whole
   * passenger-and-day cell; a panel whose rows are one booking each passes a narrower predicate so
   * approving one row does not move rows the user never looked at.
   */
  shouldApprove?: (conflict: IApprovalBookingConflict) => boolean;
}

export const RejectedBookingApprovalButtonTableCell: FC<
  IRejectedBookingApprovalButtonTableCellProps
> = ({
  passengerId,
  passengerFullName,
  date,
  errorType,
  id,
  shouldApprove,
}) => {
  const { openAsyncDialog } = useDialogs();
  const setApprovalBookingConflicts = useSetRecoilState(
    bulkApprovalBookingConflictsAtom,
  );
  const [approvalBookingConflictsUpdated, setApprovalBookingConflictsUpdated] =
    useRecoilState(bulkApprovalBookingConflictsUpdatedAtom);

  const cellId = getBookingCalendarCellId(Number(passengerId), date);

  // Only this cell's own conflicts can answer whether this row is approved, so the whole map is
  // not flattened on every poll tick just to look one id up.
  const isApproved = useMemo(
    () =>
      Boolean(
        approvalBookingConflictsUpdated
          .get(cellId)
          ?.some(conflict => conflict.id === id && conflict.approvalReason),
      ),
    [approvalBookingConflictsUpdated, cellId, id],
  );

  const setter = useCallback(
    ({
      prevState,
      approvalResult,
    }: {
      approvalResult: SimpleResult<{
        approvalReason: ReasonDtoDropdownItem;
        approver: string;
      }>;
      prevState: Map<BookingCellId, IApprovalBookingConflict[]>;
    }): Map<BookingCellId, IApprovalBookingConflict[]> => {
      const prevApprovalBookingConflicts = prevState.get(cellId) ?? [];

      const approvalBookingConflicts = prevApprovalBookingConflicts.map(
        conflict =>
          !shouldApprove || shouldApprove(conflict)
            ? {
                ...conflict,
                approvalReason: approvalResult.data
                  ?.approvalReason as ReasonDtoDropdownItem,
                approver: approvalResult.data?.approver as string,
              }
            : conflict,
      );

      return new Map([...prevState.set(cellId, approvalBookingConflicts)]);
    },
    [cellId, shouldApprove],
  );

  const onManualConfirmationClick = async (): Promise<void> => {
    const approvalResult = await openAsyncDialog<{
      approvalReason: ReasonDtoDropdownItem;
      approver: string;
    }>({
      dialogName: DialogType.BookingBulkSetConflictApprovalReason,
      dialogProps: {
        id,
        passengerId,
        passengerFullName,
        date,
        errorType,
      },
    });

    if (!approvalResult.success) {
      return;
    }

    setApprovalBookingConflictsUpdated(prevState =>
      setter({ prevState, approvalResult }),
    );
    setApprovalBookingConflicts(prevState =>
      setter({ prevState, approvalResult }),
    );
  };

  return (
    <TableCell
      data-e2e="manual-approve-cell"
      width={16}
      className="pointer"
      onClick={onManualConfirmationClick}
    >
      {isApproved && <SVG_MUI.CircleCheck5SVG />}
      {!isApproved && <SVG_MUI.CheckDouble5SVG />}
    </TableCell>
  );
};
